import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, Plus, Trash2, CheckCircle, XCircle, CheckCircle2, Edit, Power, Key } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../contexts/ToastContext';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import PortalLayout from '../../../components/PortalLayout';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import PasswordResetModal from '../../../components/PasswordResetModal';
import axios from 'axios';

const UserManagement = () => {
  const { token } = useAuth();
  const toast = useToast();
  const [users, setUsers] = useState([]);
  const [jobRoles, setJobRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showJobRoleModal, setShowJobRoleModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newJobRole, setNewJobRole] = useState('');
  const [formData, setFormData] = useState({ email: '', password: '', passwordConfirm: '', first_name: '', last_name: '', role: 'user', job_role: '' });
  const [passwordError, setPasswordError] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, action: null, title: '', message: '', data: null });
  const [passwordResetModal, setPasswordResetModal] = useState({ isOpen: false, userId: null, userName: '' });
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchUsers();
    fetchJobRoles();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/portal/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchJobRoles = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/portal/leads/job-roles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobRoles(response.data.roles);
    } catch (error) {
      console.error('Error fetching job roles:', error);
    }
  };

  const createJobRole = async () => {
    if (!newJobRole.trim()) return;
    try {
      await axios.post(`${BACKEND_URL}/api/portal/leads/job-roles`, { name: newJobRole }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewJobRole('');
      fetchJobRoles();
    } catch (error) {
      toast.error('Error creating job role');
    }
  };

  const deleteJobRole = async (roleId) => {
    if (!window.confirm('Delete this job role?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/portal/leads/job-roles/${roleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchJobRoles();
    } catch (error) {
      toast.error('Error deleting job role');
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    
    // Password confirmation check
    if (formData.password !== formData.passwordConfirm) {
      setPasswordError('Passwords do not match');
      return;
    }
    setPasswordError('');
    
    try {
      // Convert "none" to empty string for job_role
      const userData = {
        ...formData,
        job_role: formData.job_role === 'none' ? '' : formData.job_role
      };
      
      await axios.post(`${BACKEND_URL}/api/portal/users`, userData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowCreateModal(false);
      setFormData({ email: '', password: '', passwordConfirm: '', first_name: '', last_name: '', role: 'user', job_role: '' });
      fetchUsers();
      toast.success('User created successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creating user');
    }
  };

  const editUser = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '',
      passwordConfirm: '',
      first_name: user.first_name,
      last_name: user.last_name,
      role: user.role,
      job_role: user.job_role || 'none'  // Use 'none' if empty
    });
    setShowEditModal(true);
  };

  const updateUser = async (e) => {
    e.preventDefault();
    try {
      // Update user details - convert "none" to empty string
      const updateData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        role: formData.role,
        job_role: formData.job_role === 'none' ? '' : formData.job_role
      };
      
      await axios.put(`${BACKEND_URL}/api/portal/users/${editingUser.id}`, 
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowEditModal(false);
      setEditingUser(null);
      setFormData({ email: '', password: '', passwordConfirm: '', first_name: '', last_name: '', role: 'user', job_role: '' });
      fetchUsers();
      toast.success('User updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error updating user');
    }
  };

  const deleteUser = async (userId, userName) => {
    setConfirmDialog({
      isOpen: true,
      action: () => confirmDeleteUser(userId),
      title: 'Delete User?',
      message: `Are you sure you want to permanently delete ${userName}? This action cannot be undone.`,
      data: { userId, userName },
      type: 'danger'
    });
  };

  const confirmDeleteUser = async (userId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/portal/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchUsers();
      toast.success('User deleted successfully');
      setConfirmDialog({ isOpen: false, action: null, title: '', message: '', data: null });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error deleting user');
    }
  };

  const resetPassword = async (userId, userName) => {
    setPasswordResetModal({ isOpen: true, userId, userName });
  };

  const confirmPasswordReset = async (newPassword) => {
    try {
      await axios.put(`${BACKEND_URL}/api/portal/users/${passwordResetModal.userId}/reset-password`, 
        { new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Password reset successfully');
      setPasswordResetModal({ isOpen: false, userId: null, userName: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error resetting password');
    }
  };

  const toggleUserStatus = async (userId, currentStatus, userName) => {
    const action = currentStatus ? 'deactivate' : 'activate';
    setConfirmDialog({
      isOpen: true,
      action: () => confirmToggleStatus(userId),
      title: `${action === 'activate' ? 'Activate' : 'Deactivate'} User?`,
      message: `Are you sure you want to ${action} ${userName}?`,
      data: { userId, currentStatus }
    });
  };

  const confirmToggleStatus = async (userId) => {
    try {
      await axios.put(`${BACKEND_URL}/api/portal/users/${userId}/toggle-status`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchUsers();
      toast.success('User status updated successfully');
    } catch (error) {
      toast.error('Error updating user status');
    }
  };

  return (
    <PortalLayout>
      <div className="p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">User Management</h1>
            <p className="text-gray-600">Manage portal users and permissions</p>
          </div>
          <div className="flex gap-3">
            <Button onClick={() => setShowJobRoleModal(true)} variant="outline" className="border-green-600 text-green-600 hover:bg-green-50">
              Manage Job Roles
            </Button>
            <Button onClick={() => setShowCreateModal(true)} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-5 h-5 mr-2" />Create User
            </Button>
          </div>
        </div>

        {/* Posts Grid - Updated Table with Job Role */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Name</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Email</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Job Role</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">System Role</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Status</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900">{user.first_name} {user.last_name}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{user.email}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{user.job_role || '-'}</td>
                  <td className="px-6 py-4 text-sm"><span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">{user.role}</span></td>
                  <td className="px-6 py-4 text-sm">{user.is_active ? <span className="text-green-600 flex items-center"><CheckCircle className="w-4 h-4 mr-1" />Active</span> : <span className="text-red-600 flex items-center"><XCircle className="w-4 h-4 mr-1" />Inactive</span>}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <button onClick={() => editUser(user)} className="p-2 hover:bg-blue-50 rounded-lg transition-colors group relative" title="Edit user">
                        <Edit className="w-4 h-4 text-blue-600" />
                        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">Edit user</span>
                      </button>
                      <button onClick={() => toggleUserStatus(user.id, user.is_active, `${user.first_name} ${user.last_name}`)} className={`p-2 hover:bg-${user.is_active ? 'red' : 'green'}-50 rounded-lg transition-colors group relative`} title={user.is_active ? 'Deactivate user' : 'Activate user'}>
                        <Power className={`w-4 h-4 ${user.is_active ? 'text-red-600' : 'text-green-600'}`} />
                        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">{user.is_active ? 'Deactivate' : 'Activate'}</span>
                      </button>
                      <button onClick={() => resetPassword(user.id, `${user.first_name} ${user.last_name}`)} className="p-2 hover:bg-orange-50 rounded-lg transition-colors group relative" title="Reset password">
                        <Key className="w-4 h-4 text-orange-600" />
                        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">Reset password</span>
                      </button>
                      <button onClick={() => deleteUser(user.id, `${user.first_name} ${user.last_name}`)} className="p-2 hover:bg-red-50 rounded-lg transition-colors group relative" title="Delete user">
                        <Trash2 className="w-4 h-4 text-red-600" />
                        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">Delete user</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Create User Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-xl p-8 max-w-md w-full">
              <h2 className="text-2xl font-bold mb-6">Create New User</h2>
              <form onSubmit={createUser} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First Name *</Label>
                  <input 
                    id="first_name"
                    type="text" 
                    required 
                    value={formData.first_name} 
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})} 
                    className="w-full h-10 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#39baeb] focus:border-[#39baeb]" 
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Last Name *</Label>
                  <input 
                    id="last_name"
                    type="text" 
                    required 
                    value={formData.last_name} 
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})} 
                    className="w-full h-10 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#39baeb] focus:border-[#39baeb]" 
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <input 
                    id="email"
                    type="email" 
                    required 
                    value={formData.email} 
                    onChange={(e) => setFormData({...formData, email: e.target.value})} 
                    className="w-full h-10 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#39baeb] focus:border-[#39baeb]" 
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password *</Label>
                  <input 
                    id="password"
                    type="password" 
                    required 
                    value={formData.password} 
                    onChange={(e) => setFormData({...formData, password: e.target.value})} 
                    className="w-full h-10 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#39baeb] focus:border-[#39baeb]" 
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="passwordConfirm">Confirm Password *</Label>
                  <input 
                    id="passwordConfirm"
                    type="password" 
                    required 
                    value={formData.passwordConfirm} 
                    onChange={(e) => setFormData({...formData, passwordConfirm: e.target.value})} 
                    className="w-full h-10 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#39baeb] focus:border-[#39baeb]" 
                  />
                  {passwordError && <p className="text-xs text-red-600">{passwordError}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="job_role">Job Role</Label>
                  <Select value={formData.job_role} onValueChange={(value) => setFormData({...formData, job_role: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select job role..." />
                    </SelectTrigger>
                    <SelectContent>
                      {jobRoles.length > 0 ? (
                        jobRoles.map(role => (
                          <SelectItem key={role.id} value={role.name}>
                            {role.name}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="none" disabled>
                          No job roles defined yet
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">Job roles can be managed via the Manage Job Roles button</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role">System Role *</Label>
                  <Select value={formData.role} onValueChange={(value) => setFormData({...formData, role: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select role..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">User</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="super_admin">Super Admin</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">Choose the access level for this user</p>
                </div>
                <div className="flex gap-3 mt-6">
                  <Button type="submit" className="flex-1 bg-blue-600">Create User</Button>
                  <Button type="button" onClick={() => setShowCreateModal(false)} variant="outline" className="flex-1">Cancel</Button>
                </div>
              </form>
            </motion.div>
          </div>
        )}

        {/* Edit User Modal */}
        {showEditModal && editingUser && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-xl p-8 max-w-md w-full">
              <h2 className="text-2xl font-bold mb-6">Edit User</h2>
              <form onSubmit={updateUser} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="edit_first_name">First Name *</Label>
                  <input 
                    id="edit_first_name"
                    type="text" 
                    required 
                    value={formData.first_name} 
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})} 
                    className="w-full h-10 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#39baeb] focus:border-[#39baeb]" 
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_last_name">Last Name *</Label>
                  <input 
                    id="edit_last_name"
                    type="text" 
                    required 
                    value={formData.last_name} 
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})} 
                    className="w-full h-10 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#39baeb] focus:border-[#39baeb]" 
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_email">Email</Label>
                  <input 
                    id="edit_email"
                    type="email" 
                    value={formData.email} 
                    disabled 
                    className="w-full h-10 px-4 py-2 border border-gray-200 rounded-lg bg-gray-100 cursor-not-allowed text-gray-500" 
                  />
                  <p className="text-xs text-gray-500">Email cannot be changed</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_job_role">Job Role</Label>
                  <Select value={formData.job_role} onValueChange={(value) => setFormData({...formData, job_role: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select job role..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No job role</SelectItem>
                      {jobRoles.length > 0 ? (
                        jobRoles.map(role => (
                          <SelectItem key={role.id} value={role.name}>
                            {role.name}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="none" disabled>
                          No job roles defined yet
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_role">System Role *</Label>
                  <Select value={formData.role} onValueChange={(value) => setFormData({...formData, role: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select role..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">User</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="super_admin">Super Admin</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">Choose the access level for this user</p>
                </div>
                <div className="flex gap-3 mt-6">
                  <Button type="submit" className="flex-1 bg-blue-600">Update User</Button>
                  <Button type="button" onClick={() => { setShowEditModal(false); setEditingUser(null); }} variant="outline" className="flex-1">Cancel</Button>
                </div>
              </form>
            </motion.div>
          </div>
        )}

        {/* Job Role Management Modal */}
        {showJobRoleModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-xl p-8 max-w-md w-full">
              <h2 className="text-2xl font-bold mb-6">Manage Job Roles</h2>
              <div className="space-y-4">
                <div className="flex gap-2">
                  <input type="text" value={newJobRole} onChange={(e) => setNewJobRole(e.target.value)} placeholder="New job role name" className="flex-1 px-4 py-2 border rounded-lg" />
                  <Button onClick={createJobRole} className="bg-green-600 hover:bg-green-700">Add</Button>
                </div>
                <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
                  {jobRoles.map(role => (
                    <div key={role.id} className="flex items-center justify-between p-3">
                      <span className="text-sm font-medium">{role.name}</span>
                      <button onClick={() => deleteJobRole(role.id)} className="text-red-600 hover:text-red-700">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
                <Button onClick={() => setShowJobRoleModal(false)} variant="outline" className="w-full mt-4">Close</Button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Confirmation Dialog */}
        <ConfirmationDialog
          isOpen={confirmDialog.isOpen}
          onClose={() => setConfirmDialog({ isOpen: false, action: null, title: '', message: '', data: null })}
          onConfirm={confirmDialog.action}
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmText="Confirm"
          cancelText="Cancel"
          type={confirmDialog.type || "warning"}
        />

        {/* Password Reset Modal */}
        <PasswordResetModal
          isOpen={passwordResetModal.isOpen}
          onClose={() => setPasswordResetModal({ isOpen: false, userId: null, userName: '' })}
          onConfirm={confirmPasswordReset}
          userName={passwordResetModal.userName}
        />
      </div>
    </PortalLayout>
  );
};

export default UserManagement;
